import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { ROUTES, urls } from '../../urls';
import {
  Alert,
  Backdrop,
  Fade,
  Modal,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import ChatBox, { Message } from './chat-box';
import '@/theme/App.css';
import { COLORS } from '@/theme/colors';
import { T, statusTone } from '@/theme/tokens';
import { Pill } from '@/components/ds';
import DisputeParticipants from './dispute-participants';
import { getUser } from '@/lib/common';
import CloseDisputeDialog from './close-dispute-dialog';
import ResourceDetailsModal from '@/components/resource-details/resource-details-modal';
import DisputeContextDetailsModal from '@/components/dispute-details/dispute-context-details-modal';
import { formatContextValue } from '@/lib/formatters';
import {
  getContextDisplayLabel,
  isContextDisplaySectionBreak,
  isHiddenContextField,
} from '@cloudcost/shared-types/dispute-context-display';
import parse from 'html-react-parser';

const StyledModal = styled(Modal, {
  shouldForwardProp: (prop) => prop !== 'customZIndex',
})<{ customZIndex?: number }>(({ theme, customZIndex }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: customZIndex ? customZIndex : theme.zIndex.modal + 1,
  '&:focus': {
    outline: 'none',
  },
  '& .MuiBackdrop-root': {
    zIndex: customZIndex ? customZIndex - 1 : undefined,
  },
  '& .MuiModal-backdrop': {
    zIndex: customZIndex ? customZIndex - 1 : undefined,
  },
  '& .MuiModal-root': {
    zIndex: customZIndex ? customZIndex : undefined,
  },
}));

const ModalPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'customZIndex',
})<{ customZIndex?: number }>(({ theme, customZIndex }) => ({
  backgroundColor: COLORS.white,
  // !important to beat the global .MuiPaper-rounded{border-radius:14px!important}
  borderRadius: '16px !important',
  boxShadow: '0 24px 60px rgba(7,68,108,.32)',
  boxSizing: 'border-box',
  margin: '10px',
  width: '760px',
  maxWidth: '760px',
  height: '82vh',
  maxHeight: '90vh',
  position: 'relative',
  zIndex: customZIndex ? customZIndex + 1 : theme.zIndex.modal + 2,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  animation: 'ccDisputeIn .22s cubic-bezier(.2,.7,.3,1) both',
  '@keyframes ccDisputeIn': {
    from: { transform: 'translateY(14px) scale(.985)' },
    to: { transform: 'none' },
  },
  '&:focus': {
    outline: 'none',
  },
  [theme.breakpoints.down('md')]: {
    width: '95%',
    height: '90vh',
  },
}));

// Single-column, message-first dispute conversation view.
export default function DisputesModal(props: any) {
  const {
    Dispute: initialDispute,
    initialContext: initialContextProp,
    IsModalOpened,
    onCloseModal,
    zIndex,
  } = props;

  const [availableUsers, setAvailableUsers] = useState([]);
  const [disputeStatus, setDisputeStatus] = useState<string>(
    initialDispute.status,
  );
  const [actionCenterData, setActionCenterData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [firstMessageLoad, setFirstMessageLoad] = useState(false);
  const [disputeMembers, setDisputeMembers] = useState([]);
  const [closeDisputeDialogOpen, setCloseDisputeDialogOpen] = useState(false);
  const [disputeResources, setDisputeResources] = useState<any[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [disputeContext, setDisputeContext] = useState<{
    initial: Record<string, any> | null;
    current: Record<string, any>;
    snapshots?: Array<{ captured_at: string; context: Record<string, any> }>;
  } | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);

  const user = getUser();
  const navigate = useNavigate();
  const canManageParticipants =
    Number(user?.id) === Number(initialDispute.sender_id);
  
  // Get the dispute ID - prefer id_dispute if available, otherwise use id
  const disputeId = initialDispute.id_dispute ?? initialDispute.id;
  const disputeName = parse(initialDispute.action_center_name ? initialDispute.action_center_name : initialDispute.subject) 

  const resource = useMemo(() => {
    return {
      ...initialDispute,
      action_type: initialDispute.type,
      date_created_txt: initialDispute.date_created_txt,
      id: initialDispute.id, // This may be action center item ID when opened from action center
      id_recommendation: initialDispute.id_recommendation,
      responsible_id:
        initialDispute.responsible_id ?? initialDispute.id_techowner,
      id_budget: initialDispute.id_budget,
    };
  }, [initialDispute]);

  const fetchMessages = useCallback(async () => {
    try {
      const data: Message[] = await fetchWrapper.post(
        urls.DevOps.getMessages,
        {
          id: disputeId,
        },
      );
      data.forEach(message => {
        if (message.id_user_created===null){
          message.username='System Info';
        }
      });
      setMessages(data);
      if (data.length > 0) {
        const lastMessageId = data.reduce((maxId, message) => {
          const numericId = Number(message.id);
          if (Number.isNaN(numericId)) {
            return maxId;
          }
          return Math.max(maxId, numericId);
        }, 0);

        if (lastMessageId > 0) {
          await fetchWrapper.post(urls.DevOps.markMessagesRead, {
            disputeId,
            lastMessageId,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setFirstMessageLoad(true);
    }
  }, [disputeId]);

  const fetchDisputeUsers = useCallback(async () => {
    try {
      const users = await fetchWrapper.post(urls.DevOps.getDisputeUsers, {
        id: disputeId,
      });
      setDisputeMembers(users);
      const available = await fetchWrapper.post(
        urls.DevOps.getAvailableDisputeUsers,
        { id: disputeId },
      );
      setAvailableUsers(available);
    } catch (error) {
      console.error('Error fetching dispute users:', error);
    }
  }, [disputeId]);

  /**
   * Fetches resources linked to this dispute
   */
  /**
   * Fetches resources linked to this dispute (only for Resource type disputes)
   */
  const fetchDisputeResources = useCallback(async () => {
    // Only fetch resources for "Resource" type disputes
    if (!disputeId || initialDispute.type !== 'Resource') {
      setDisputeResources([]);
      setLoadingResources(false);
      return;
    }
    
    setLoadingResources(true);
    try {
      const url = urls.DevOps.getResourcesByDisputeId.replace(':disputeId', disputeId.toString());
      console.log('Fetching resources for dispute:', disputeId, 'URL:', url);
      const resources = await fetchWrapper.get(url);
      console.log('Fetched dispute resources:', resources);
      // Ensure we have an array
      const resourcesArray = Array.isArray(resources) ? resources : [];
      setDisputeResources(resourcesArray);
    } catch (error) {
      console.error('Error fetching dispute resources:', error);
      setDisputeResources([]);
    } finally {
      setLoadingResources(false);
    }
  }, [disputeId, initialDispute.type]);

  /**
   * Handles opening resource details modal
   */
  const handleOpenResourceDetails = (resourceId: number) => {
    setSelectedResourceId(resourceId);
    setResourceModalOpen(true);
  };

  /**
   * Fetches dispute with context (initial/current) from getDispute when modal opens.
   */
  const fetchDisputeContext = useCallback(async () => {
    if (!disputeId) return;
    setLoadingContext(true);
    setDisputeContext(null);
    try {
      const response: any = await fetchWrapper.get(
        urls.DevOps.getDispute.replace(':id', String(disputeId)),
      );
      const context = response?.context;
      if (context && typeof context === 'object') {
        setDisputeContext({
          initial: context.initial ?? null,
          current: context.current ?? {},
          snapshots: Array.isArray(context.snapshots) ? context.snapshots : [],
        });
      }
    } catch (err) {
      console.error('Error fetching dispute context:', err);
    } finally {
      setLoadingContext(false);
    }
  }, [disputeId]);

  useEffect(() => {
    if (IsModalOpened) {
      fetchDisputeUsers();
      fetchDisputeResources();
      if (
        initialContextProp &&
        typeof initialContextProp === 'object' &&
        initialContextProp.current != null
      ) {
        setDisputeContext({
          initial: initialContextProp.initial ?? null,
          current: initialContextProp.current ?? {},
          snapshots: Array.isArray(initialContextProp.snapshots)
            ? initialContextProp.snapshots
            : [],
        });
      } else {
        fetchDisputeContext();
      }
      setDisputeStatus(initialDispute.status);
    }
  }, [
    IsModalOpened,
    initialDispute.id,
    initialDispute.status,
    fetchDisputeUsers,
    fetchDisputeResources,
    fetchDisputeContext,
    initialContextProp,
  ]);

  const assignOwner = async (userId: string) => {
    try {
      await fetchWrapper.post(urls.DevOps.addDisputeUser, {
        user: userId,
        dispute: disputeId,
      });
      await fetchWrapper.post(urls.DevOps.createNotification, {
        id_dispute: disputeId,
        id_user: userId,
        body: `Please have a look at the dispute #${disputeId}`,
        title: 'You were added to an existing dispute',
        type: 'dispute',
      });
      fetchDisputeUsers(); // Refresh both lists
    } catch (error) {
      console.error('Error assigning owner:', error);
    }
  };

  const updateDisputeStatus = async (status: string) => {
    try {
      await fetchWrapper.post(urls.DevOps.updateDispute, {
        id: disputeId,
        status,
      });
      setDisputeStatus(status);
      setShowAlert(true);
      onCloseModal(null);
    } catch (error) {
      console.error('Error updating dispute:', error);
    }
  };

  const handleCloseDisputeClick = () => {
    setCloseDisputeDialogOpen(true);
  };

  const handleCloseDisputeConfirm = () => {
    setCloseDisputeDialogOpen(false);
    updateDisputeStatus('Closed');
  };

  const handleCloseDisputeCancel = () => {
    setCloseDisputeDialogOpen(false);
  };

  const deleteDisputeMember = async (userId: string) => {
    try {
      await fetchWrapper.post(urls.DevOps.deleteUserFromDispute, {
        id_user: userId,
        id_dispute: disputeId,
      });
      fetchDisputeUsers();
    } catch (error) {
      console.error('Error deleting dispute member:', error);
    }
  };

  const handleModalClose = useCallback(
    (event: any) => {
      onCloseModal(event, { name: 'example', type: 'closed from child' });
      setShowAlert(false);
      setAvailableUsers([]);
      setDisputeStatus(initialDispute.status);
      setActionCenterData([]);
      setDisputeMembers([]);
      setDisputeContext(null);
      setContextModalOpen(false);
    },
    [onCloseModal, initialDispute.status],
  );

  const handleOnCancel = useCallback(() => {
    fetchWrapper
      .post(urls.DevOps.deleteDispute, {
        id: disputeId,
      })
      .then((data) => {
        handleModalClose(null);
      })
      .catch((error) => console.error('There was an error!', error));
  }, [disputeId, handleModalClose]);

  const getResourceUrl = (resource: any) => {
    switch (resource.action_type.toLowerCase()) {
      case 'attestation':
        return `${ROUTES.ATTESTATION}?action_item=${resource.id}`;
      case 'budget':
        return `${ROUTES.BUDGETS}?action_item=${resource.id}`;
      case 'allocation':
        return `${ROUTES.ALLOCATION}?action_item=${resource.id}`;
      case 'recommendation':
        return `${ROUTES.OPTIMIZATION}?action_item=${resource.id}`;
      default:
        return `${ROUTES.DASHBOARD}?action_item=${resource.id}`;
    }
  };

  const iconBtnSx = {
    width: 34,
    height: 34,
    borderRadius: '8px',
    border: '1px solid #E1E7F0',
    color: '#5A6472',
    '&:hover': { backgroundColor: '#F2F7FD' },
  };

  // Context metadata → inline pill chips (skip section-break markers).
  const ctxChips =
    disputeContext?.current && typeof disputeContext.current === 'object'
      ? Object.entries(disputeContext.current)
          .filter(([k]) => !isContextDisplaySectionBreak(k) && !isHiddenContextField(k))
          .map(([k, v]) => ({
            label: getContextDisplayLabel(k),
            value: formatContextValue(v),
          }))
      : [];

  return (
    <>
      <StyledModal
        open={IsModalOpened}
        onClose={handleModalClose}
        closeAfterTransition
        customZIndex={zIndex}
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: 'rgba(16,35,58,.5)',
              zIndex: zIndex ? zIndex - 1 : undefined,
            },
          },
        }}
        disableAutoFocus>
        <Fade in={IsModalOpened}>
          <ModalPaper customZIndex={zIndex}>
            {/* 1. Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                flexShrink: 0,
                p: '20px 24px 16px',
              }}>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                  }}>
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: 17,
                      fontWeight: 900,
                      color: '#16233A',
                      lineHeight: 1.3,
                    }}>
                    Dispute Case #{disputeId} — {disputeName}
                  </Typography>
                  <Pill tone={statusTone(disputeStatus)}>{disputeStatus}</Pill>
                </Box>
                <Typography sx={{ mt: 0.5, fontSize: 12.5, color: '#8A94A6' }}>
                  Opened by {initialDispute.sender || 'Unknown'}
                  {initialDispute.date_created_txt
                    ? ` · ${initialDispute.date_created_txt}`
                    : ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}>
                {Number(user?.id) === Number(initialDispute.sender_id) &&
                  firstMessageLoad &&
                  messages.length === 0 && (
                    <Button
                      variant="outlined"
                      onClick={handleOnCancel}
                      color="error"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '8px',
                        boxShadow: 'none',
                      }}>
                      Cancel Creation
                    </Button>
                  )}
                {Number(user?.id) === Number(initialDispute.sender_id) &&
                  messages.length > 0 && (
                    <Button
                      variant="contained"
                      onClick={handleCloseDisputeClick}
                      color="primary"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '8px',
                        boxShadow: 'none',
                      }}>
                      End Dispute and mark as Closed
                    </Button>
                  )}
                <Tooltip title="Copy link to clipboard">
                  <IconButton
                    onClick={() => {
                      const url = `${window.location.origin}${ROUTES.DASHBOARD}?dispute=${disputeId}`;
                      navigator.clipboard.writeText(url);
                    }}
                    sx={iconBtnSx}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleModalClose}
                  aria-label="Close dispute details"
                  sx={iconBtnSx}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* 2. Context strip */}
            <Box
              sx={{
                flexShrink: 0,
                p: '0 24px 14px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}>
              {loadingContext ? (
                <Typography sx={{ fontSize: 12.5, color: '#8A94A6' }}>
                  Loading context…
                </Typography>
              ) : (ctxChips.length > 0
                  ? ctxChips
                  : [{ label: 'Status', value: disputeStatus }]
                ).map((c, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: '#F5F8FC',
                    border: '1px solid #EBF0F6',
                    borderRadius: '999px',
                    fontSize: 12.5,
                  }}>
                  <Box component="span" sx={{ color: '#8A94A6', fontWeight: 700 }}>
                    {c.label}
                  </Box>
                  <Box component="span" sx={{ color: '#16233A', fontWeight: 800 }}>
                    {c.value}
                  </Box>
                </Box>
              ))}

              {initialDispute.type === 'Resource' &&
                disputeResources.map((resourceItem: any) => (
                  <Chip
                    key={resourceItem.id}
                    label={
                      resourceItem.resource_name || `Resource #${resourceItem.id}`
                    }
                    onClick={() => handleOpenResourceDetails(resourceItem.id)}
                    sx={{
                      cursor: 'pointer',
                      height: 28,
                      borderRadius: 999,
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: T.primary,
                      backgroundColor: T.primaryTint,
                      border: 'none',
                      '& .MuiChip-label': { px: 1.25 },
                      '&:hover': { backgroundColor: T.primary, color: '#fff' },
                    }}
                    size="small"
                  />
                ))}

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setContextModalOpen(true)}
                  disabled={(disputeContext?.snapshots?.length ?? 0) <= 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12.5,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    color: '#16233A',
                    borderColor: '#D8E0EA',
                    '&:hover': {
                      borderColor: '#9FC2F0',
                      backgroundColor: '#F2F7FD',
                    },
                  }}>
                  Context history
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(getResourceUrl(resource))}
                  disabled={
                    !resource?.action_type ||
                    !['attestation', 'budget', 'allocation', 'recommendation'].includes(
                      String(resource.action_type).toLowerCase(),
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12.5,
                    borderRadius: '8px',
                    boxShadow: 'none',
                  }}>
                  Explore data
                </Button>
              </Box>
            </Box>

            {/* 3. Participants row */}
            <Box
              sx={{
                flexShrink: 0,
                p: '12px 24px',
                borderTop: '1px solid #EEF1F6',
                borderBottom: '1px solid #EEF1F6',
                background: '#FCFDFF',
              }}>
              <DisputeParticipants
                sender={{
                  name: initialDispute.sender,
                  photo: initialDispute.senderphoto,
                }}
                receiver={{
                  name: initialDispute.receiver,
                  photo: initialDispute.receiverphoto,
                }}
                disputeMembers={disputeMembers}
                availableUsers={availableUsers}
                canManageParticipants={canManageParticipants}
                onAssignOwner={assignOwner}
                onDeleteDisputeMember={deleteDisputeMember}
              />
            </Box>

            {/* 4 + 5. Messages + composer (the hero) */}
            <Box
              sx={{
                flex: '1 1 0',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <ChatBox
                DisputeNumber={disputeId}
                fetchMessages={fetchMessages}
                messages={messages}
                setMessages={setMessages}
              />
            </Box>

            {showAlert && (
              <Alert
                severity="success"
                onClick={() => setShowAlert(false)}
                sx={{ m: '0 24px 12px' }}>
                Saving Successful
              </Alert>
            )}
          </ModalPaper>
        </Fade>
      </StyledModal>

      <CloseDisputeDialog
        open={closeDisputeDialogOpen}
        onClose={handleCloseDisputeCancel}
        onConfirm={handleCloseDisputeConfirm}
      />

      <ResourceDetailsModal
        open={resourceModalOpen}
        onClose={() => {
          setResourceModalOpen(false);
          setSelectedResourceId(null);
        }}
        resourceId={selectedResourceId}
        zIndex={zIndex ? zIndex + 200 : 1500}
        openedFromDispute={true}
      />
      <DisputeContextDetailsModal
        open={contextModalOpen}
        setOpen={setContextModalOpen}
        disputeId={disputeId ?? null}
      />
    </>
  );
}
